import { AlertCircle, Camera, FileImage, Upload, X, Check } from "lucide-react";

// CMR Upload Modal
export const CMRUploadModal = ({ show, course, cmrImage, uploading, cmrType, onClose, onFileSelect, onUpload, onRemoveImage, fileInputRef }) => {
  if (!show) return null;

  const isStartCMR = cmrType === 'start';
  const modalTitle = isStartCMR ? 'CMR Pornire' : 'CMR Finalizare';
  
  const getColorClasses = () => {
    if (isStartCMR) {
      return {
        gradient: 'from-orange-600/20 to-amber-600/20',
        bg: 'bg-orange-500/20',
        text: 'text-orange-400',
        border: 'border-orange-500/20',
        hover: 'hover:border-orange-400/50',
        hoverBg: 'hover:from-orange-500/10 hover:to-amber-500/10',
        activeBg: 'from-orange-500/5 to-amber-500/5',
        buttonBg: 'from-orange-500/20 to-amber-500/20',
        buttonHover: 'hover:from-orange-500/30 hover:to-amber-500/30',
        buttonActive: 'active:from-orange-500/40 active:to-amber-500/40',
        buttonBorder: 'border-orange-500/30',
        buttonText: 'text-orange-300',
        tipsBg: 'from-orange-500/10 to-amber-500/10',
        tipsBorder: 'border-orange-500/20',
        tipsText: 'text-orange-300',
        tipsContent: 'text-orange-200/90'
      };
    }
    return {
      gradient: 'from-purple-600/20 to-violet-600/20',
      bg: 'bg-purple-500/20',
      text: 'text-purple-400',
      border: 'border-purple-500/20',
      hover: 'hover:border-purple-400/50',
      hoverBg: 'hover:from-purple-500/10 hover:to-violet-500/10',
      activeBg: 'from-purple-500/5 to-violet-500/5',
      buttonBg: 'from-purple-500/20 to-violet-500/20',
      buttonHover: 'hover:from-purple-500/30 hover:to-violet-500/30',
      buttonActive: 'active:from-purple-500/40 active:to-violet-500/40',
      buttonBorder: 'border-purple-500/30',
      buttonText: 'text-purple-300',
      tipsBg: 'from-purple-500/10 to-violet-500/10',
      tipsBorder: 'border-purple-500/20',
      tipsText: 'text-purple-300',
      tipsContent: 'text-purple-200/90'
    };
  };
  
  const colors = getColorClasses();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-end sm:items-center sm:justify-center">
      <div className="bg-gradient-to-b from-slate-900/95 to-slate-800/95 backdrop-blur-xl rounded-t-3xl sm:rounded-3xl border-t sm:border border-white/10 w-full sm:max-w-lg max-h-[85vh] sm:max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`bg-gradient-to-r ${colors.gradient} border-b border-white/10 p-5 sm:p-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center`}>
                <FileImage className={`w-5 h-5 ${colors.text}`} />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg sm:text-xl">{modalTitle}</h3>
                <p className="text-gray-400 text-xs sm:text-sm">{course?.name || 'Document transport'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all active:scale-95"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 overflow-y-auto max-h-[calc(85vh-80px)] sm:max-h-[calc(90vh-100px)]">
          <div className="space-y-5">
            {!cmrImage ? (
              <div className="space-y-4">
                {/* Upload Area */}
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={`group border-2 border-dashed border-white/20 ${colors.hover} rounded-2xl p-8 sm:p-12 text-center cursor-pointer transition-all active:scale-[0.98] bg-gradient-to-br ${colors.activeBg} ${colors.hoverBg}`}
                >
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${colors.buttonBg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    <Camera className={`w-8 h-8 sm:w-10 sm:h-10 ${colors.text}`} />
                  </div>
                  <p className="text-white font-semibold text-base sm:text-lg mb-2">Fă o poză {modalTitle}</p>
                  <p className="text-gray-400 text-sm sm:text-base max-w-xs mx-auto">Apasă pentru a deschide camera sau selecta o imagine din galerie</p>
                </div>

                {/* Info Card */}
                <div className={`bg-gradient-to-br ${colors.tipsBg} border ${colors.tipsBorder} rounded-2xl p-4 sm:p-5`}>
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <AlertCircle className={`w-4 h-4 ${colors.text}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`${colors.tipsText} font-semibold mb-2 text-sm sm:text-base`}>Sfaturi pentru o poză perfectă</h4>
                      <ul className={`${colors.tipsContent} text-xs sm:text-sm space-y-2`}>
                        <li className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Documentul să fie complet vizibil</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Evită umbrele și reflexii</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Fotografie clară și lizibilă</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Max. 10MB</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Image Preview */}
                <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 rounded-2xl p-4 sm:p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <FileImage className="w-4 h-4 text-green-400" />
                      </div>
                      <span className="text-white font-semibold text-sm sm:text-base">Previzualizare</span>
                    </div>
                    <div className="px-3 py-1 bg-green-500/20 rounded-full">
                      <span className="text-green-300 text-xs font-medium">Gata</span>
                    </div>
                  </div>
                  
                  <div className="relative rounded-xl overflow-hidden bg-black/30 border border-white/5">
                    <img 
                      src={cmrImage.preview} 
                      alt="CMR Preview" 
                      className="w-full max-h-80 object-contain"
                    />
                  </div>
                  
                  <div className="mt-3 flex items-center space-x-2">
                    <div className="flex-1 bg-white/5 rounded-lg px-3 py-2">
                      <p className="text-gray-400 text-xs sm:text-sm truncate">{cmrImage.name}</p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={onRemoveImage}
                    disabled={uploading}
                    className="bg-red-500/10 hover:bg-red-500/20 active:bg-red-500/30 text-red-300 border border-red-500/30 rounded-xl py-3.5 sm:py-4 px-4 font-semibold disabled:opacity-50 transition-all active:scale-95 text-sm sm:text-base"
                  >
                    Șterge
                  </button>
                  <button
                    onClick={onUpload}
                    disabled={uploading}
                    className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 active:from-green-500/40 active:to-emerald-500/40 text-green-300 border border-green-500/30 rounded-xl py-3.5 sm:py-4 px-4 font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 transition-all active:scale-95 text-sm sm:text-base"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-300/30 border-t-green-300"></div>
                        <span>Se încarcă...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Încarcă</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Success Info */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4">
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <p className="text-green-300 text-sm font-medium">Documentul este gata de încărcare</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFileSelect}
        className="hidden"
      />
    </div>
  );
};